import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../lib/api.js';
import FilterSidebar from '../components/FilterSidebar.jsx';
import BusinessDetailPanel from '../components/BusinessDetailPanel.jsx';
import useCampaign from '../hooks/useCampaign.js';

// Creighton District center (roughly Thomas & 32nd)
const DEFAULT_CENTER = [33.489, -112.020];
const DEFAULT_ZOOM = 14;

// Color by prospect status
const STATUS_COLORS = {
  identified: '#9ca3af',  // gray
  contacted: '#3b82f6',   // blue
  interested: '#a855f7',  // purple
  committed: '#10b981',   // emerald
  declined: '#ef4444',    // red
  default: '#6b7280',     // slate
};

// Cache icons by color
const iconCache = {};
function markerIcon(color) {
  if (iconCache[color]) return iconCache[color];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24C24 5.373 18.627 0 12 0z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="4.5" fill="#ffffff"/>
  </svg>`;
  const icon = L.divIcon({
    html: svg,
    className: 'ccf-marker',
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -30],
  });
  iconCache[color] = icon;
  return icon;
}

export default function MapPage() {
  const { campaignId } = useCampaign();
  const [filters, setFilters] = useState({});
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['businesses', filters, campaignId],
    queryFn: () =>
      api.listBusinesses({
        ...filters,
        campaign_id: campaignId,
        limit: 3000,
      }),
  });

  const businesses = data?.businesses || [];

  // Only render markers with coords
  const pinned = useMemo(
    () => businesses.filter((b) => b.latitude && b.longitude),
    [businesses]
  );

  return (
    <div className="h-full flex">
      <FilterSidebar filters={filters} onChange={setFilters} campaignId={campaignId} />

      <div className="flex-1 relative">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={50}
            showCoverageOnHover={false}
          >
            {pinned.map((b) => {
              const color = STATUS_COLORS[b.prospect_status] || STATUS_COLORS.default;
              return (
                <Marker
                  key={b.id}
                  position={[b.latitude, b.longitude]}
                  icon={markerIcon(color)}
                  eventHandlers={{
                    click: () => setSelectedId(b.id),
                  }}
                >
                  <Popup>
                    <div className="text-sm max-w-xs">
                      <div className="font-semibold">{b.name}</div>
                      <div className="text-gray-600">{b.address}</div>
                      {b.primary_sic_description && (
                        <div className="text-gray-500 text-xs mt-1">
                          {b.primary_sic_description}
                        </div>
                      )}
                      {b.annual_revenue && (
                        <div className="text-gray-500 text-xs">
                          Revenue: ${b.annual_revenue.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MarkerClusterGroup>
        </MapContainer>

        {/* Count overlay */}
        <div className="absolute top-3 right-3 z-[1000] bg-white rounded shadow px-3 py-2 text-sm">
          {isLoading ? 'Loading...' : `${pinned.length} businesses`}
        </div>
      </div>

      {/* Detail panel */}
      {selectedId && (
        <BusinessDetailPanel
          businessId={selectedId}
          campaignId={campaignId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
